import { AlertCircle, CheckCircle2, Download, X } from "lucide-react";
import React, { useState } from "react";
import AppButton from "../../../components/ui/AppButton";
import AppInputField from "../../../components/ui/AppInputField";
import DateRangePicker from "../../../components/ui/DateRangePicker";
import { Modal } from "../../../components/ui/Modal";
import { MySelect } from "../../../components/ui/Select";
import { useToast } from "../../../hooks/useToast";
import { downloadFile } from "../../../lib/exportUtil";
import { cn } from "../../../lib/utils";
import { FuelLogExportBody } from "../@types";
import { useExportFuelLogsMutation } from "../fuelLogApiSlice";

interface ExportFuelLogModalProps {
  vehicleId?: string;
}

const ExportFuelLogModal: React.FC<ExportFuelLogModalProps> = ({
  vehicleId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dates, setDates] = useState({
    startDate: "",
    endDate: "",
  });

  const [fuelType, setFuelType] = useState<string>("");
  const [stationName, setStationName] = useState<string>("");
  const [format, setFormat] = useState<string>("");
  const [validationError, setValidationError] = useState<string>("");

  const toast = useToast();

  const [exportFuelLog, { isLoading, isSuccess, isError, error, data }] =
    useExportFuelLogsMutation();

  // Auto-download file when data is available
  //   useEffect(() => {
  //     if (isSuccess && data) {
  //       downloadFile(data, format);
  //     }
  //   }, [isSuccess, data, format]);

  const validateForm = (): boolean => {
    if (!dates.startDate || !dates.endDate) {
      setValidationError("Please select a date range");
      return false;
    }

    if (new Date(dates.startDate) > new Date(dates.endDate)) {
      setValidationError("Start date must be before end date");
      return false;
    }

    if (!format) {
      setValidationError("Please select an export format");
      return false;
    }

    setValidationError("");
    return true;
  };

  const handleExport = async () => {
    if (!validateForm()) return;

    const payload: FuelLogExportBody = {
      startDate: dates.startDate,
      endDate: dates.endDate,
      vehicleId: vehicleId ?? undefined,
      fuelType: fuelType || undefined,
      stationName: stationName || undefined,
      documentType: format.toLowerCase(),
    };

    try {
      const blob = await exportFuelLog(payload).unwrap();

      const fileName = `fuel-log-report`;

      downloadFile(blob, format, fileName);

      toast.success({
        title: "Success",
        message: `Fuel log report exported successfully as ${format}`,
      });
    } catch (err: any) {
      const errorMessage =
        err?.data?.message || err?.message || "Failed to export fuel log data";

      toast.error({
        title: "Export Failed",
        message: errorMessage,
      });

      console.error("Export error:", err);
    }
  };

  const getErrorMessage = () => {
    if (validationError) return validationError;
    if (isError && error) {
      return (
        (error as any)?.data?.message || "An error occurred while exporting"
      );
    }
    return "";
  };

  const isFormValid = dates.startDate && dates.endDate && format;

  return (
    <div className="">
      <div className="">
        <AppButton
          appearance="soft"
          onClick={() => setIsOpen(true)}
          className="flex items-center px-4 py-2 border border-primary bg-white rounded-lg text-primary hover:bg-gray-50 transition-colors min-w-[146px] h-[38px]"
        >
          <Download className="h-4 w-4 mr-2" />
          Export csv
        </AppButton>
      </div>

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        className="p-6 max-w-xl mx-auto"
      >
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-5">
          {/* Header */}
          <div className="flex justify-between items-center gap-2">
            <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Download className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Export Fuel Log Report
                </h3>
                <p className="text-sm text-gray-500">
                  Download fuel log data in your preferred format
                </p>
              </div>
            </div>

            <div className="text-red-500 bg-red-100 rounded-md flex items-center justify-center px-2">
              <button onClick={() => setIsOpen(false)}>
                <X className="w-4 h-4s" />
              </button>
            </div>
          </div>

          {/* Form Fields */}
          <div className="space-y-4">
            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date Range <span className="text-red-500">*</span>
              </label>
              <DateRangePicker
                value={dates}
                onChange={(newDates) => {
                  setDates(newDates);
                  setValidationError("");
                }}
              />
            </div>

            {/* Fuel Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fuel Type
              </label>
              <MySelect<string>
                name="fuel-type"
                id="fuel-type-select"
                label=""
                options={["All", "Petrol", "Diesel"]}
                getValue={(option: string) => option}
                getName={(option: string) => option}
                value={fuelType}
                onChange={(e) => setFuelType(e)}
                onValChange={(e) => setFuelType(e)}
                placeholder="Select fuel type (optional)"
                className={cn(
                  "h-[44px] text-[15px] px-3 text-gray-700 appearance-none bg-white border border-gray-200 rounded-lg",
                  "focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent",
                  "hover:border-gray-300 transition-colors"
                )}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Station name
              </label>
              <AppInputField
                type="text"
                name="station-name"
                id="station-name"
                placeholder="Enter station name (optional)"
                onChange={(e) => setStationName(e.target.value)}
                value={stationName}
                className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
              />
            </div>

            {/* Format Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Export Format <span className="text-red-500">*</span>
              </label>
              <MySelect<string>
                name="format"
                id="format-select"
                label=""
                options={["Excel", "PDF"]}
                getValue={(option: string) => option}
                getName={(option: string) => {
                  if (option === "Excel") return "📊 Excel Spreadsheet (.xlsx)";
                  return "📄 PDF Document (.pdf)";
                }}
                value={format}
                onChange={(e) => {
                  setFormat(e);
                  setValidationError("");
                }}
                onValChange={(e) => {
                  setFormat(e);
                  setValidationError("");
                }}
                placeholder="Select export format"
                className={cn(
                  "h-[44px] text-[15px] px-3 text-gray-700 appearance-none bg-white border border-gray-200 rounded-lg",
                  "focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent",
                  "hover:border-gray-300 transition-colors"
                )}
              />
            </div>

            {/* Error Message */}
            {(validationError || isError) && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-lg">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-800">Error</p>
                  <p className="text-sm text-red-600">{getErrorMessage()}</p>
                </div>
              </div>
            )}

            {/* Success Message */}
            {isSuccess && (
              <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-100 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-800">Success!</p>
                  <p className="text-sm text-green-600">
                    Your file is being downloaded...
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Export Button */}
          <button
            onClick={handleExport}
            disabled={isLoading || !isFormValid}
            className={cn(
              "w-full h-11 flex items-center justify-center gap-2 rounded-lg font-medium text-white transition-all",
              "focus:outline-none focus:ring-2 focus:ring-offset-2",
              isFormValid && !isLoading
                ? "bg-primary hover:bg-primary/80 focus:ring-primary shadow-sm hover:shadow"
                : "bg-gray-300 cursor-not-allowed",
              isLoading && "opacity-75"
            )}
          >
            {isLoading ? (
              <>
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span>Exporting...</span>
              </>
            ) : (
              <>
                <Download className="w-5 h-5" />
                <span>Export Report</span>
              </>
            )}
          </button>

          {/* Info Text */}
          <p className="text-xs text-gray-500 text-center">
            The file will download automatically once the export is complete
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default ExportFuelLogModal;
